import React, { useEffect, useRef, useState } from 'react';
import { Flex, message, Skeleton, Spin } from 'antd';
import { CustomButton, CustomModalWithoutButton } from '@/components';
import { MaterialRouteIcon } from '@/assets/icons/icons';
import type { LatLng, RouteDeviationVariant } from '@/apis/types';
import {
  DEFAULT_MILES_OFF,
  groupingMiles,
  useDeleteRouteDeviation,
  useRouteDeviationCandidates,
  useRouteDeviationSetting,
  useSaveRouteDeviation,
} from './queries';
import RouteDeviationMap from './RouteDeviationMap';
import RouteDeviationToleranceFields, {
  DEFAULT_MINUTES_OFF,
  isValidMiles,
} from './RouteDeviationToleranceFields';
import RouteDeviationVariantList, {
  isTicked,
  mostUsedLabel,
} from './RouteDeviationVariantList';
import styles from './RouteDeviation.module.css';
import {
  LANE_CHANGED,
  LOADING_ROUTES,
  LOAD_FAILED,
  NO_PAST_TRIPS,
  PICK_A_ROUTE,
  REMOVED,
  REMOVE_CONFIRM,
  REMOVE_FAILED,
  RETRY,
  SAVED,
  SAVE_FAILED,
  SETUP_TITLE,
  SLOW_SENSOR,
  SUGGESTED_ROUTES_TITLE,
  UNCHECKABLE_MESSAGES,
  builtFrom,
  cityOf,
  refusalMessage,
} from './routeDeviationCopy';
import {
  isClientRefusal,
  isThrottled,
  refusalReason,
} from './routeDeviationErrors';

export interface RouteDeviationLaneStop {
  name: string;
  address?: string;
  latlng?: LatLng | null;
}

interface RouteDeviationSetupModalProps {
  open: boolean;
  shipmentId: string;
  pickup?: RouteDeviationLaneStop;
  delivery?: RouteDeviationLaneStop;
  /** The shipment's last known position, when the drawer already has one. */
  currentPosition?: LatLng | null;
  onClose: () => void;
}

type SetupBodyProps = Omit<RouteDeviationSetupModalProps, 'open'>;

// How long the miles field must sit still before the routes are regrouped.
const REGROUP_DEBOUNCE_MS = 500;

// One array for every render without groups, so the map is not refitted each time.
const NO_VARIANTS: RouteDeviationVariant[] = [];

const BUTTON_LOOK = {
  height: '36px',
  minWidth: '60px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '16px',
  boxShadow: 'none',
  padding: '10px 12px',
};

const SetupBody: React.FC<SetupBodyProps> = ({
  shipmentId,
  pickup,
  delivery,
  currentPosition,
  onClose,
}) => {
  const setting = useRouteDeviationSetting(shipmentId, true);
  const saved = setting.data?.setting ?? null;
  const laneChanged = !saved && setting.data?.reason === 'lane_changed';
  // The routes are searched at the grouping distance, which only the miles
  // under 3 change, so a tolerance of 3 or more reuses the same search. The
  // user's edits move it after a pause; until then it follows the saved setting.
  const [regroupedAt, setRegroupedAt] = useState<number | null>(null);
  const searchMiles =
    regroupedAt ?? groupingMiles(saved?.miles_off ?? DEFAULT_MILES_OFF);
  const candidates = useRouteDeviationCandidates(
    shipmentId,
    searchMiles,
    setting.isSuccess
  );

  const variants = candidates.data?.variants ?? NO_VARIANTS;
  const totalDeliveries = variants.reduce(
    (sum, variant) => sum + variant.trip_count,
    0
  );
  const loadError = setting.isError
    ? setting.error
    : candidates.isError
      ? candidates.error
      : null;
  const failed = setting.isError || candidates.isError;
  const loading = !failed && !candidates.isSuccess;
  // A new grouping distance shows the previous groups until its own arrive.
  const regrouping = candidates.isPlaceholderData;
  // A lane the search cannot check answers with a code instead of routes.
  const blockedMessage = candidates.data
    ? UNCHECKABLE_MESSAGES[candidates.data.checkable]
    : undefined;
  const noPastTrips =
    candidates.isSuccess && !blockedMessage && variants.length === 0;
  // A failed read ends in the query's error state, so there is nothing to catch.
  const ignore = (): void => undefined;
  const retryLoad = () => {
    if (setting.isError) setting.refetch().catch(ignore);
    if (candidates.isError) candidates.refetch().catch(ignore);
  };

  const [miles, setMiles] = useState<number | null>(DEFAULT_MILES_OFF);
  const [minutes, setMinutes] = useState(DEFAULT_MINUTES_OFF);
  // The form starts on the saved values once they arrive (the same miles the
  // candidates were asked for), and is the user's from then on.
  const [prefilled, setPrefilled] = useState(false);
  if (saved && !prefilled) {
    setPrefilled(true);
    setMiles(saved.miles_off);
    setMinutes(saved.minutes_off);
    // Miles typed before the setting arrived are replaced, so the search goes
    // back to the saved setting's grouping too.
    setRegroupedAt(null);
  }

  // Ticks are kept as trip IDs, so they survive a regrouping of the routes.
  // Until the user ticks something, a saved setting ticks every group that
  // shares a trip with it; a new one ticks the most used group.
  const [pickedTripIds, setPickedTripIds] =
    useState<ReadonlySet<string> | null>(null);
  const mostUsed = mostUsedLabel(variants);
  const savedTripIds = new Set(saved?.variants.flatMap((v) => v.trip_ids));
  const defaultTicked = saved
    ? variants.filter((v) => isTicked(v, savedTripIds))
    : variants.filter((v) => v.label === mostUsed);
  const tickedTripIds: ReadonlySet<string> =
    pickedTripIds ?? new Set(defaultTicked.flatMap((v) => v.trip_ids));
  // The latest ticks, for the regroup timer to freeze when it fires.
  const tickedRef = useRef(tickedTripIds);
  tickedRef.current = tickedTripIds;
  const searchMilesRef = useRef(searchMiles);
  searchMilesRef.current = searchMiles;
  // Ticks are only worth freezing once there are groups to read them from.
  const loadedRef = useRef(candidates.isSuccess);
  loadedRef.current = candidates.isSuccess;
  // The miles on screen, for the regroup timer: a prefill can replace them
  // while the timer waits.
  const milesRef = useRef(miles);
  milesRef.current = miles;
  const regroupTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(regroupTimer.current), []);
  const handleMilesChange = (value: number | null) => {
    setMiles(value);
    clearTimeout(regroupTimer.current);
    if (!isValidMiles(value)) return;
    regroupTimer.current = setTimeout(() => {
      if (!isValidMiles(milesRef.current)) return;
      const next = groupingMiles(milesRef.current);
      if (next === searchMilesRef.current) return;
      // Keep what is ticked as trips, so the new groups tick by what they share
      // with it rather than by the default for the new groups. Before the first
      // groups load there is nothing ticked yet, so the defaults still apply.
      if (loadedRef.current) {
        setPickedTripIds((picked) => picked ?? new Set(tickedRef.current));
      }
      setRegroupedAt(next);
    }, REGROUP_DEBOUNCE_MS);
  };

  const toggleVariant = (variant: RouteDeviationVariant) => {
    const next = new Set(tickedTripIds);
    const ticked = isTicked(variant, tickedTripIds);
    variant.trip_ids.forEach((id) => (ticked ? next.delete(id) : next.add(id)));
    setPickedTripIds(next);
  };

  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const save = useSaveRouteDeviation(shipmentId);
  const remove = useDeleteRouteDeviation(shipmentId);

  const tripIds = variants
    .filter((variant) => isTicked(variant, tickedTripIds))
    .flatMap((variant) => variant.trip_ids);
  const nothingTicked =
    candidates.isSuccess && variants.length > 0 && tripIds.length === 0;
  const canSave =
    candidates.isSuccess &&
    !blockedMessage &&
    tripIds.length > 0 &&
    isValidMiles(miles) &&
    // During the pause before a regroup the routes on screen are still the old
    // groups, so their trips would not match the new tolerance.
    groupingMiles(miles) === searchMiles &&
    !regrouping &&
    !save.isPending &&
    !remove.isPending;

  const handleSave = () => {
    if (!canSave || !isValidMiles(miles)) return;
    setSaveError(null);
    save.mutate(
      { miles_off: miles, minutes_off: minutes, trip_ids: tripIds },
      {
        onSuccess: () => {
          message.success(SAVED);
          onClose();
        },
        onError: (error) => {
          setSaveError(refusalMessage(error, SAVE_FAILED));
          // The routes changed under the form, so its ticks may name trips
          // that are gone: read them again. A changed lane also drops the
          // saved setting, so that is read again too.
          const reason = refusalReason(error);
          if (reason === 'lane_changed' || reason === 'unknown_trip') {
            candidates.refetch().catch(ignore);
          }
          if (reason === 'lane_changed') {
            // Dropping the saved setting must not move the search back to the
            // default miles while the field still holds the user's.
            if (isValidMiles(miles)) setRegroupedAt(groupingMiles(miles));
            setting.refetch().catch(ignore);
          }
        },
      }
    );
  };

  const handleRemove = () => {
    setSaveError(null);
    remove.mutate(undefined, {
      onSuccess: () => {
        message.success(REMOVED);
        onClose();
      },
      onError: (error) => setSaveError(refusalMessage(error, REMOVE_FAILED)),
    });
  };

  return (
    <div className={styles.panes}>
      <Flex vertical gap={24} className={styles.formPane}>
        {laneChanged && (
          <div className={styles.notice} role='status'>
            {LANE_CHANGED}
          </div>
        )}
        {(pickup || delivery) && (
          <div>
            <p className={styles.laneName}>
              {[pickup?.name, delivery?.name].filter(Boolean).join(' → ')}
            </p>
            {pickup?.address && (
              <p className={styles.laneAddress}>{`From: ${pickup.address}`}</p>
            )}
            {delivery?.address && (
              <p className={styles.laneAddress}>{`To: ${delivery.address}`}</p>
            )}
          </div>
        )}

        <Flex vertical gap={8}>
          <span className={styles.sectionLabel}>Route configuration</span>
          <div className={styles.card}>
            <span className={styles.cardIcon}>
              <MaterialRouteIcon />
            </span>
            <div>
              <div className={styles.cardTitle}>{SUGGESTED_ROUTES_TITLE}</div>
              {candidates.isSuccess && variants.length > 0 && (
                <div className={styles.cardSubtitle}>
                  {builtFrom(totalDeliveries)}
                </div>
              )}
            </div>
          </div>
        </Flex>

        {blockedMessage && (
          <div className={styles.notice} role='status'>
            {blockedMessage}
          </div>
        )}

        {!blockedMessage && (
          <Flex vertical gap={8}>
            <span className={styles.sectionLabel}>Select routes</span>
            {loading && (
              <>
                <Skeleton active paragraph={{ rows: 3 }} title={false} />
                <span className={styles.state}>{LOADING_ROUTES}</span>
              </>
            )}
            {failed && (
              <Flex vertical align='flex-start' gap={8}>
                <span className={styles.fieldError} role='alert'>
                  {isClientRefusal(loadError) || isThrottled(loadError)
                    ? refusalMessage(loadError, LOAD_FAILED)
                    : LOAD_FAILED}
                </span>
                {!isClientRefusal(loadError) && (
                  <CustomButton
                    text={RETRY}
                    onClick={retryLoad}
                    bgColor='transparent'
                    className={styles.ghostButton}
                    color='var(--text-primary)'
                    {...BUTTON_LOOK}
                  />
                )}
              </Flex>
            )}
            {candidates.data?.checkable === 'slow_sensor' && (
              <div className={styles.notice} role='status'>
                {SLOW_SENSOR}
              </div>
            )}
            {noPastTrips && (
              <span className={styles.state}>{NO_PAST_TRIPS}</span>
            )}
            {candidates.isSuccess && (
              // The delay keeps a quick regroup from flashing the spinner.
              <Spin spinning={regrouping} delay={300}>
                <RouteDeviationVariantList
                  variants={variants}
                  tickedTripIds={tickedTripIds}
                  onToggle={toggleVariant}
                />
              </Spin>
            )}
          </Flex>
        )}

        {!blockedMessage && (
          <RouteDeviationToleranceFields
            miles={miles}
            minutes={minutes}
            onMilesChange={handleMilesChange}
            onMinutesChange={setMinutes}
          />
        )}

        {saveError && (
          <span className={styles.fieldError} role='alert'>
            {saveError}
          </span>
        )}

        <div className={styles.footer}>
          {confirmingRemove ? (
            <>
              <span className={`${styles.footerStart} ${styles.footerHint}`}>
                {REMOVE_CONFIRM}
              </span>
              <CustomButton
                text='Keep'
                onClick={() => setConfirmingRemove(false)}
                disabled={remove.isPending}
                bgColor='transparent'
                className={styles.ghostButton}
                color='var(--text-primary)'
                {...BUTTON_LOOK}
              />
              <CustomButton
                text='Yes, remove'
                onClick={handleRemove}
                disabled={remove.isPending}
                loader={remove.isPending ? <Spin size='small' /> : null}
                bgColor='var(--danger)'
                color='white'
                {...BUTTON_LOOK}
              />
            </>
          ) : (
            <>
              <div className={styles.footerStart}>
                {saved && (
                  <CustomButton
                    text='Remove'
                    onClick={() => setConfirmingRemove(true)}
                    disabled={save.isPending}
                    bgColor='transparent'
                    className={styles.ghostButton}
                    color='var(--danger)'
                    {...BUTTON_LOOK}
                  />
                )}
                {nothingTicked && (
                  <span className={styles.footerHint}>{PICK_A_ROUTE}</span>
                )}
              </div>
              <CustomButton
                text='Cancel'
                onClick={onClose}
                disabled={save.isPending}
                bgColor='transparent'
                className={styles.ghostButton}
                color='var(--text-primary)'
                {...BUTTON_LOOK}
              />
              {!blockedMessage && (
                <CustomButton
                  text={saved ? 'Save' : 'Create'}
                  onClick={handleSave}
                  disabled={!canSave}
                  loader={save.isPending ? <Spin size='small' /> : null}
                  bgColor='#000'
                  color='#fff'
                  {...BUTTON_LOOK}
                />
              )}
            </>
          )}
        </div>
      </Flex>
      <div className={styles.mapPane}>
        <RouteDeviationMap
          variants={variants}
          tickedTripIds={tickedTripIds}
          pickup={pickup?.latlng}
          delivery={delivery?.latlng}
          pickupName={pickup?.name}
          pickupCity={cityOf(pickup?.address)}
          deliveryName={delivery?.name}
          currentPosition={currentPosition}
        />
      </div>
    </div>
  );
};

const RouteDeviationSetupModal: React.FC<RouteDeviationSetupModalProps> = ({
  open,
  shipmentId,
  pickup,
  delivery,
  currentPosition,
  onClose,
}) => (
  <CustomModalWithoutButton
    modalOpen={open}
    setModalOpen={(nextOpen) => {
      if (!nextOpen) onClose();
    }}
    modalTitle={SETUP_TITLE}
    modalSize={1183}
    className={styles.modal}
    modalBorderRadius='16px'
    fontSize='24px'
    lineHeight='32px'
    closeIconSize='16px'
    destroyOnHidden
  >
    {/* antd keeps a hidden modal's children, so destroyOnHidden is what gives each opening fresh state and queries. */}
    {open && (
      <SetupBody
        shipmentId={shipmentId}
        pickup={pickup}
        delivery={delivery}
        currentPosition={currentPosition}
        onClose={onClose}
      />
    )}
  </CustomModalWithoutButton>
);

export default RouteDeviationSetupModal;
